<template>
  <div>
    <LoadingOverlay
      :data-test-id="$testId('loadingOverlay')"
      :visible="showLoadingOverlay"
      :zIndex="1001"
    />

    <UseCaseOnboarding
      v-if="!isSkipping"
    />
  </div>
</template>

<script>
import {
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import { LoadingOverlay } from '@jumpcloud/ui-components';
import {
  chipGroupFeatures,
  defaultOnboardingFeatures,
  mspDefaultFeatures,
  onboardingFeatures,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { experiments } from 'client/GrowthEngineering/OptimizelyApi';
import { implementationChecklists } from 'client/components/ImplementationChecklist/ImplementationChecklists';
import { mapActions, mapGetters, mapState } from 'vuex';
import ChecklistMixin from 'client/components/ImplementationChecklist/mixins/ChecklistMixin';
import LoginModalController, { LoginModalControllerConfig } from 'components/Modal/LoginModalController';
import MspState from 'client/components/models/MspState';
import OnboardingTasks from 'client/GrowthEngineering/OnboardingTasks';
import OptimizelyHelper from 'client/GrowthEngineering/OptimizelyHelper';
import UseCaseOnboarding from 'client/components/UseCaseOnboarding/UseCaseOnboarding.vue';
import Util from 'client/util/Util';

const growthModule = 'GrowthModule';
const onboardingModule = 'OnboardingModule';
const orgCollection = StoreModuleMap[ItemTypeConsts.organizations];
const partnersModule = StoreModuleMap[ItemTypeConsts.partners];
const settingsModule = 'SettingsModule';

const { createAdminAccount } = defaultOnboardingFeatures;
const defaultChecklistFeature = implementationChecklists[createAdminAccount];

const { isAnMsp } = MspState;

const {
  landingPageReferralOnboarding,
} = experiments;

export default {
  name: 'OnboardingContainer',

  components: {
    LoadingOverlay,
    UseCaseOnboarding,
  },

  mixins: [ChecklistMixin],

  data() {
    return {
      isLoadingOnboardingExperiments: true,
      isLoadingPartners: true,
      isSkipping: true,
      showLandingPageReferralOnboarding: false,
    };
  },

  OptimizelyHelper,

  computed: {
    ...mapGetters(onboardingModule, {
      onboardingFlowComplete: 'getOnboardingFlowComplete',
    }),

    ...mapGetters(orgCollection, {
      orgCreatedDate: 'getOrgCreatedDate',
    }),

    ...mapGetters(partnersModule, [
      'hasActiveCrowdStrikeLink',
      'hasPendingCrowdStrikeTrial',
    ]),

    ...mapState(growthModule, [
      'optimizelyLoaded',
    ]),

    ...mapState(settingsModule, [
      'orgId',
      'user',
    ]),

    firstMspSession() {
      return MspState.isAnMsp && this.user.sessionCount === 1;
    },

    isRemoteAssistDirect() {
      return this.directFeatureOnboarding(onboardingFeatures.remoteAssist);
    },

    showLoadingOverlay() {
      // only show the overlay if we show onboarding
      // tests can get into a state where onboardingFlowComplete is false but bypasses
      // the onboarding component which will still show the overlay on top of the app
      return (this.isLoadingOnboardingExperiments || this.isLoadingPartners)
        && !this.onboardingFlowComplete;
    },
  },

  watch: {
    hasActiveCrowdStrikeLink(value) {
      this.skipOnboarding(value);
    },

    hasPendingCrowdStrikeTrial(value) {
      this.skipOnboarding(value);
    },

    // if Optimizely wasn't loaded when the component was created then
    // show a loading screen until it loads
    optimizelyLoaded(isLoaded) {
      if (isLoaded) {
        this.handleMounted();
        if (this.handleMountedTimeout) clearTimeout(this.handleMountedTimeout);
      }
    },
  },

  LoginModalController,

  created() {
    this.handleFetchPartners();
  },

  mounted() {
    // check if Optimizely has loaded when the component is created
    if (this.optimizelyLoaded) {
      this.handleMounted();
    } else {
      // if Optimizely doesn't load just move on
      this.handleMountedTimeout = setTimeout(() => {
        this.handleMounted();
      }, 5000);
    }
  },

  methods: {
    ...mapActions(onboardingModule, [
      'addOnboardingTasks',
      'setInitialChecklistFeatures',
      'setShowChecklistLoading',
      'updateDirectOnboardingFeature',
      'updateImplementationChecklistDismissed',
      'updateOnboardingFlowComplete',
    ]),

    ...mapActions(partnersModule, [
      'fetchPartners',
    ]),

    async handleFetchPartners() {
      await this.fetchPartners();
      this.isLoadingPartners = false;
    },

    handleMounted() {
      // handle MSP onboarding, init checklist, redirect to discover
      if (MspState.isAnMsp) {
        this.setMSPOnboarding();
      } else if (!this.onboardingFlowComplete && !MspState.isAnMsp) {
        this.setOnboardingExperiments();
        // If the account came in from the Remote Assist product landing page,
        // and they are not in the landing page referral experiment variation,
        // then skip onboarding,
        // load Remote Assist to their checklists, and show the loading modal
        if (
          this.isRemoteAssistDirect
            && !this.isLoadingOnboardingExperiments
            && !this.showLandingPageReferralOnboarding
        ) {
          this.setDirectOnboardingForFeature(onboardingFeatures.remoteAssist);
        } else {
          // defaults to skipping until we are sure we need to show onboarding,
          // this prevents component flashing
          this.isSkipping = false;
        }
      }

      const config = new LoginModalControllerConfig({
        isAnMsp,
        onboardingFlowComplete: this.onboardingFlowComplete,
        orgCreatedDate: this.orgCreatedDate,
      });

      this.$options.LoginModalController(config);

      // If this.onboardingFlowComplete is true, ensure all onboarding flow flags are set properly
      // This probably should be handled in the OnboardingModule automatically at some point
      if (this.onboardingFlowComplete) {
        this.updateOnboardingFlowComplete();
      }
    },

    /**
     * TODO - this functionality should live in the Growth/Onboarding module
     * We should not enter /onboarding if user is RA direct
     */
    setDirectOnboardingForFeature(feature) {
      this.updateDirectOnboardingFeature(feature);
      // load the remote assist feature to their checklist and launch the checklistloading modal
      const preSelectedFeatures = chipGroupFeatures
        .filter(item => item.value === feature);
      const featureNames = preSelectedFeatures.map(item => item.name);
      featureNames.unshift(defaultChecklistFeature.feature);
      this.initializeImplementationChecklists(preSelectedFeatures);

      this.skipOnboarding(true);

      this.setInitialChecklistFeatures(featureNames);
      this.setShowChecklistLoading(true);
    },

    setLandingPageReferralOnboarding() {
      if (landingPageReferralOnboarding.eligibilityCheck()) {
        this.showLandingPageReferralOnboarding = this.$options.OptimizelyHelper
          .getExperimentVariation(landingPageReferralOnboarding);

        // If they are eligible and in the variation the show direct onboarding for their feature
        if (this.showLandingPageReferralOnboarding) {
          const landingPageOnboardingFeature = this.getLandingPageOnboardingFeature();
          this.setDirectOnboardingForFeature(landingPageOnboardingFeature);
        }
      }
    },

    setMSPOnboarding() {
      if (this.firstMspSession) {
        this.initializeImplementationChecklists(mspDefaultFeatures);
        this.setTasks();
      }
      this.skipOnboarding(true);
    },

    setOnboardingExperiments() {
      this.setLandingPageReferralOnboarding();
      this.isLoadingOnboardingExperiments = false;
    },

    setTasks() {
      // Populate store with onboarding tasks to be completed
      this.addOnboardingTasks({
        checkLocalStorage: false,
        tasks: OnboardingTasks.getOnboardingTaskList(true),
      });
    },

    skipOnboarding(value) {
      if (value && !this.onboardingFlowComplete) {
        this.updateOnboardingFlowComplete();
        this.updateImplementationChecklistDismissed(true);
        Util.redirect(this.redirectUrl);
      }
    },
  },
};
</script>
